import { JSX, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { Link } from "react-router-dom";
import { NetworkIntegrityAggregateTable, NetworkIntegrityWidgetInfoFlyout, ProvidersByZipCodeClassificationDescription, WidgetMenu } from ".";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityAggregateResults, NetworkIntegrityAggregateTableRow } from "@app/projects/network-assessment/network-integrity/models";
import { NetworkIntegrityActionStatus } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkIntegrityAggregateGroup, NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { getPendoClassNameForAggregateGroup } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityPendoHelper";
import { shouldShowViewAllFooter } from "@app/projects/network-assessment/network-integrity/helpers/viewAllFooterHelper";
import { WidgetLoading, WidgetHeader, WidgetFooter, Widget, WidgetBody } from "./Widget";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

const ProvidersByZipCodeClassWidget = (): JSX.Element => {
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const ffContext = useContext(FeatureFlagContext);
  const showTermedStatus = ffContext.enabledFeatureFlags.has(Features.DISPLAY_NETWORK_INTEGRITY_TOTAL_AFTER_TERMED);
  const project = networkIntegrityContext.project!;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);

  const [providersByZipCodeClassResults, setProvidersByZipCodeClassResult] = useState<NetworkIntegrityAggregateResults>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const title = 'Providers By Zip Code Classification';

  useEffect(() => {
    let cancelled = false;
    setProvidersByZipCodeClassResult(null);
    async function fetchProvidersByZipCodeClassResults() {
      try {
        if (networkIntegrityContext.projectRunConfigurationId) {
          const result = await networkIntegrityService.getProvidersByZipCodeClassResults(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);
          if (!cancelled) setProvidersByZipCodeClassResult(result);
        }
      } finally {
        if (!cancelled) setLoading(false);
      };
    };

    if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
      setLoading(true);
      fetchProvidersByZipCodeClassResults();
    }

    return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading><WidgetHeader title={title} /></WidgetLoading>);

  const showFooter = (): ReactNode => {
    return shouldShowViewAllFooter(showTermedStatus, providersByZipCodeClassResults?.totalAggregateCount) &&
      <WidgetFooter>
        <Link className={getPendoClassNameForAggregateGroup(NetworkIntegrityAggregateGroup.ProvidersByZipClass, "view-all")}
          to={{
            pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/all/${NetworkIntegrityAggregateGroup.ProvidersByZipClass}`,
            state: {
              initialProviderType: networkIntegrityContext.providerType,
              initialRegionSetIds: networkIntegrityContext.regionSetIds,
              initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
              runConfiguration: networkIntegrityContext.projectRunConfiguration
            }
          }}>
            View All
        </Link>
      </WidgetFooter>
  }

  const rows: NetworkIntegrityAggregateTableRow[] = providersByZipCodeClassResults?.networkIntegrityAggregates.map((result, index) => {
    const aggregateKey = result.aggregateKey;

    const getProviderDetailsLink = (count: number, actionStatus?: NetworkIntegrityActionStatus) => {
      return (count != 0 ?
        <Link className={`${getPendoClassNameForAggregateGroup(NetworkIntegrityAggregateGroup.ProvidersByZipClass, index.toString())}`}
          to={{
            pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/providers/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.ProvidersByZipClass}/${encodeURIComponent(result.aggregateKey)}`,
            state: {
              initialActionStatus: actionStatus,
              initialRegionSetIds: networkIntegrityContext.regionSetIds,
              initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
              runConfiguration: networkIntegrityContext.projectRunConfiguration
            }
          }}>
          {count?.toLocaleString()}
        </Link> :
        <span>0</span>);
    };

    const count = result ? getProviderDetailsLink(result.count) : undefined;
    const afterTermedCount = result?.afterTermedCount != null ? getProviderDetailsLink(result.afterTermedCount, NetworkIntegrityActionStatus.NoActionsApplied) : undefined;

    return { afterTermedCount, aggregateKey, count, rawAfterTermedCount: result?.afterTermedCount ?? undefined, rawCount: result?.count };
  });

  return (
    <Widget>
      <WidgetHeader title={title} >
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title={title} description={<ProvidersByZipCodeClassificationDescription />} />
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        <NetworkIntegrityAggregateTable aggregateHeader="Zip Class" aggregateCountHeader="Unique Access Points" rows={rows} />
      </WidgetBody>
      {showFooter()}
    </Widget>);
}

export { ProvidersByZipCodeClassWidget };